import { useNavigate } from 'react-router-dom'

import { Box, Button, StatusLabel, Text, Title } from '@factorialco/deprecated-design-system'
import { JournalLineReconciliationFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'

import { getReconcilableJournalLine } from '../../_utils'
import type { InvoiceType } from '../../types'
import ReconciliationTable from './_ReconciliationTable'

export const ReconciliationSection = ({
  invoice,
  showReconciliationSection,
  canReconcileWithJournalLines,
  canReconcileWithFinancialDocuments,
  journalLines,
}: {
  invoice: InvoiceType
  showReconciliationSection: boolean
  canReconcileWithJournalLines: boolean
  canReconcileWithFinancialDocuments: boolean
  journalLines: JournalLineReconciliationFragment[]
}) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const { access } = useCurrentSession()

  const canShowReconciliation =
    showReconciliationSection && (invoice.reconcilable || invoice.fullyReconciledAt)

  if (!canShowReconciliation) {
    return <></>
  }

  const reconciliations = invoice.reconciliations || []

  const journalLine = getReconcilableJournalLine(invoice, journalLines)
  const reconcilableReconciliations = journalLine?.reconcilableReconciliations || []

  const goToReconcile = () => {
    if ('id' in invoice && invoice.id) {
      navigate(
        navigation.finance.invoices.list.invoiceId.reconciliations.fullPath({
          invoiceId: invoice.id,
        })
      )
    }
  }

  const reconciliationAmount = canReconcileWithJournalLines
    ? reconcilableReconciliations.reduce((total, reconciliation) => {
        return total + (reconciliation.reconcilableReconciledAmountCents || 0)
      }, 0)
    : reconciliations.reduce((total, reconciliation) => {
        return total + (reconciliation.reconcilableReconciledAmountCents || 0)
      }, 0)

  const pendingAmount = Math.abs(invoice.totalAmountCents) - reconciliationAmount

  const reconcilableItems = canReconcileWithJournalLines
    ? reconcilableReconciliations.map((reconciliation) => {
        const bankTransaction = reconciliation.journalLine?.journalEntry?.bankTransaction
        const bankAccountId = bankTransaction?.bankAccount?.id
        const transactionId = bankTransaction?.id

        return {
          id: reconciliation.id,
          bookingDate: bankTransaction?.bookingDate || '',
          description: bankTransaction?.description || '',
          reconcilableReconciledAmountCents: reconciliation.reconcilableReconciledAmountCents || 0,
          reconcilableCurrency: invoice.currency || '',
          url:
            bankAccountId && transactionId
              ? navigation.finance.banking.accounts.accountId.transactions.transactionId.fullPath({
                  id: bankAccountId,
                  transaction_id: transactionId,
                })
              : '',
        }
      })
    : reconciliations.map((reconciliation) => ({
        id: reconciliation.id,
        bookingDate: reconciliation.transaction.bookingDate,
        description: reconciliation.transaction.description || '',
        reconcilableReconciledAmountCents: reconciliation.reconcilableReconciledAmountCents || 0,
        reconcilableCurrency: reconciliation.reconcilableCurrency || '',
        url: navigation.finance.banking.accounts.accountId.transactions.transactionId.fullPath({
          id: reconciliation.transaction.bankAccount.id,
          transaction_id: reconciliation.transaction.id,
        }),
      }))

  return (
    <Box gap="s16" paddingTop="s8">
      <Box flexDirection="row" justifyContent="spaceBetween" alignItems="flexStart">
        <Title
          variant="400"
          title={i18n.t('finance.invoices.detail.reconciliations.transactions_associated')}
          description={i18n.t('finance.invoices.detail.reconciliations.select_a_transaction')}
        />

        {pendingAmount > 0 ? (
          invoice.currency ? (
            <Text size="200" weight="normal" color="grey1000">
              {i18n.t('finance.invoices.detail.reconciliations.pending_amount', {
                amount: formattedAmount({
                  amountCents: pendingAmount,
                  currency: invoice.currency,
                  locale: access.locale,
                }),
              })}
            </Text>
          ) : null
        ) : (
          <Text size="200" weight="normal" color="grey1000">
            <StatusLabel type={'positive'}>
              {i18n.t('finance.invoices.detail.reconciliations.paid')}
            </StatusLabel>
          </Text>
        )}
      </Box>

      <ReconciliationTable reconcilableItems={reconcilableItems} />

      {(canReconcileWithFinancialDocuments || canReconcileWithJournalLines) && (
        <Button
          onClick={goToReconcile}
          type="secondary"
          iconName="add"
          disabled={!invoice.currency || !invoice.totalAmountCents}
        >
          {i18n.t('finance.invoices.detail.reconciliations.add_transaction')}
        </Button>
      )}
    </Box>
  )
}
